import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showProjectsDropdown, setShowProjectsDropdown] = useState(false);
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  
  const currentLanguage = i18n.language;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    setIsMobileMenuOpen(false);
    setShowProjectsDropdown(false);
  };

  const goToHome = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const openAboutModal = () => {
    setShowAboutModal(true);
    setIsMobileMenuOpen(false);
    document.body.style.overflow = 'hidden';
  };

  const closeAboutModal = () => {
    setShowAboutModal(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'bg-white shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="w-full px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button 
              onClick={goToHome}
              className="flex items-center gap-3 cursor-pointer"
            >
              <img 
                src="https://static.readdy.ai/image/5a473773e458290c64cd1d32a10fb3f8/b5df3a4822e45a1a85c46f7903d22975.jpeg" 
                alt="Ernado Logo" 
                className="h-10 w-auto object-contain"
              />
              <span 
                className={`text-3xl font-extralight tracking-[0.2em] transition-colors ${
                  isScrolled ? 'text-gray-900' : 'text-white'
                }`}
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                ERNADO
              </span>
            </button>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-10">
              {/* Projects Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setShowProjectsDropdown(true)}
                onMouseLeave={() => setShowProjectsDropdown(false)}
              >
                <button
                  onClick={(e) => { e.preventDefault(); scrollToSection('projects'); }}
                  className={`transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap ${
                    isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                  }`}
                >
                  {t('nav.projects')}
                  <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${showProjectsDropdown ? 'rotate-180' : ''}`}></i>
                </button>

                {/* Dropdown Menu */}
                {showProjectsDropdown && (
                  <div className="absolute top-full left-0 mt-2 w-56 rounded-xl shadow-lg overflow-hidden bg-white">
                    <button
                      onClick={(e) => { e.preventDefault(); scrollToSection('furniture'); setShowProjectsDropdown(false); }}
                      className="w-full text-left block px-6 py-3 text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {t('nav.furniture')}
                    </button>
                    <button
                      onClick={(e) => { e.preventDefault(); scrollToSection('dis-cephe'); setShowProjectsDropdown(false); }}
                      className="w-full text-left block px-6 py-3 text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {t('nav.exterior')}
                    </button>
                  </div>
                )}
              </div>

              <a
                href="#ar-technology"
                onClick={(e) => { e.preventDefault(); scrollToSection('ar-technology'); }}
                className={`transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
              >
                {t('nav.ar')}
              </a>
              <a
                href="#news"
                onClick={(e) => { e.preventDefault(); scrollToSection('haberler'); }}
                className={`transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
              >
                {t('nav.news')}
              </a>

              {/* About Us Button */}
              <button
                onClick={openAboutModal}
                className={`px-4 py-2 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  isScrolled 
                    ? 'bg-gray-900 text-white hover:bg-gray-800' 
                    : 'bg-white text-gray-900 hover:bg-gray-100'
                }`}
              >
                {t('nav.about')}
              </button>

              {/* Language Switcher */}
              <div className="hidden lg:flex items-center space-x-2">
                <button
                  onClick={() => changeLanguage('tr')}
                  className={`px-3 py-1 rounded-md text-sm transition-all cursor-pointer whitespace-nowrap ${
                    currentLanguage === 'tr'
                      ? isScrolled ? 'text-gray-900 font-bold' : 'text-white font-bold'
                      : isScrolled ? 'text-gray-500 hover:text-gray-900' : 'text-white/60 hover:text-white'
                  }`}
                >
                  TR
                </button>
                <span className={`${isScrolled ? 'text-gray-300' : 'text-white/40'}`}>|</span>
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-3 py-1 rounded-md text-sm transition-all cursor-pointer whitespace-nowrap ${
                    currentLanguage === 'en'
                      ? isScrolled ? 'text-gray-900 font-bold' : 'text-white font-bold'
                      : isScrolled ? 'text-gray-500 hover:text-gray-900' : 'text-white/60 hover:text-white'
                  }`}
                >
                  EN
                </button>
                <span className={`${isScrolled ? 'text-gray-300' : 'text-white/40'}`}>|</span>
                <button
                  onClick={() => changeLanguage('de')}
                  className={`px-3 py-1 rounded-md text-sm transition-all cursor-pointer whitespace-nowrap ${
                    currentLanguage === 'de'
                      ? isScrolled ? 'text-gray-900 font-bold' : 'text-white font-bold'
                      : isScrolled ? 'text-gray-500 hover:text-gray-900' : 'text-white/60 hover:text-white'
                  }`}
                >
                  DE
                </button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden text-2xl transition-colors cursor-pointer ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}
            >
              <i className={isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-white md:hidden">
          <div className="flex flex-col items-center justify-center h-full space-y-8">
            <a
              href="#projects"
              onClick={(e) => { e.preventDefault(); scrollToSection('projects'); }}
              className="text-2xl font-light text-gray-900 hover:text-gray-600 transition-colors cursor-pointer"
            >
              {t('nav.projects')}
            </a>
            <a
              href="#furniture"
              onClick={(e) => { e.preventDefault(); scrollToSection('furniture'); }}
              className="text-xl font-light text-gray-600 hover:text-gray-900 transition-colors cursor-pointer pl-6"
            >
              {t('nav.furniture')}
            </a>
            <a
              href="#exterior"
              onClick={(e) => { e.preventDefault(); scrollToSection('dis-cephe'); }}
              className="text-xl font-light text-gray-600 hover:text-gray-900 transition-colors cursor-pointer pl-6"
            >
              {t('nav.exterior')}
            </a>
            <a
              href="#ar-technology"
              onClick={(e) => { e.preventDefault(); scrollToSection('ar-technology'); }}
              className="text-2xl font-light text-gray-900 hover:text-gray-600 transition-colors cursor-pointer"
            >
              {t('nav.ar')}
            </a>
            <a
              href="#news"
              onClick={(e) => { e.preventDefault(); scrollToSection('haberler'); }}
              className="text-2xl font-light text-gray-900 hover:text-gray-600 transition-colors cursor-pointer"
            >
              {t('nav.news')}
            </a>
            
            {/* Mobile About Us Button */}
            <button
              onClick={openAboutModal}
              className="text-2xl font-light text-gray-900 hover:text-gray-600 transition-colors cursor-pointer"
            >
              {t('nav.about')}
            </button>
            
            {/* Mobile Language Selector */}
            <div className="flex items-center space-x-4 pt-8 border-t border-gray-200">
              <button 
                onClick={() => {
                  changeLanguage('tr');
                  setIsMobileMenuOpen(false);
                }}
                className={`text-lg transition-colors cursor-pointer ${
                  i18n.language === 'tr' ? 'text-gray-900 font-medium' : 'text-gray-400'
                }`}
              >
                TR
              </button>
              <span className="text-gray-400">|</span>
              <button 
                onClick={() => {
                  changeLanguage('en');
                  setIsMobileMenuOpen(false);
                }}
                className={`text-lg transition-colors cursor-pointer ${
                  i18n.language === 'en' ? 'text-gray-900 font-medium' : 'text-gray-400'
                }`}
              >
                EN
              </button>
              <span className="text-gray-400">|</span>
              <button 
                onClick={() => {
                  changeLanguage('de');
                  setIsMobileMenuOpen(false);
                }}
                className={`text-lg transition-colors cursor-pointer ${
                  i18n.language === 'de' ? 'text-gray-900 font-medium' : 'text-gray-400'
                }`}
              >
                DE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* About Us Modal */}
      {showAboutModal && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={closeAboutModal}
        >
          <div
            className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeAboutModal}
              className="absolute top-6 right-6 z-10 w-12 h-12 flex items-center justify-center bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            {/* Modal Content */}
            <div className="p-8 md:p-16">
              {/* Header */}
              <div className="mb-12 text-center">
                <h1 
                  className="text-5xl md:text-6xl font-extralight tracking-[0.2em] mb-4"
                  style={{ 
                    fontFamily: "'Playfair Display', serif",
                    background: 'linear-gradient(135deg, #D4AF37 0%, #F5E6D3 50%, #C9A961 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  ERNADO
                </h1>
                <p className="text-xl text-gray-600 font-light">
                  {currentLanguage === 'tr' && 'Mimarlık & İç Mimarlık'}
                  {currentLanguage === 'en' && 'Architecture & Interior Design'}
                  {currentLanguage === 'de' && 'Architektur & Innenarchitektur'}
                </p>
              </div>

              {/* About Content */}
              <div className="space-y-8 text-gray-700 leading-relaxed">
                <div>
                  <h2 className="text-3xl font-light mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {currentLanguage === 'tr' && 'Hakkımızda'}
                    {currentLanguage === 'en' && 'About Us'}
                    {currentLanguage === 'de' && 'Über Uns'}
                  </h2>
                  <p className="text-lg">
                    {currentLanguage === 'tr' && 'ERNADO, iç mimari ve mobilya tasarımı alanında , estetik ile işlevselliği aynı potada buluşturan çağdaş bir tasarım markasıdır. Kurulduğu günden bu yana , yaşam alanlarını yalnızca görsel olarak değil; kullanım , konfor ve süreklilik açısından da dönüştürmeyi hedefler. Tasarımı yaşam kalitesini doğrudan ele alırız . Bu yaklaşım , ERNADO\'NUN tüm projelerinde tutarlı , dengeli ve zamansız bir dil oluşturmasını sağlar.'}
                    {currentLanguage === 'en' && 'ERNADO is a contemporary design brand that brings together aesthetics and functionality in the field of interior architecture and furniture design. Since its establishment, it aims to transform living spaces not only visually, but also in terms of usage, comfort and sustainability. We directly address design as quality of life. This approach enables ERNADO to create a consistent, balanced and timeless language in all its projects.'}
                    {currentLanguage === 'de' && 'ERNADO ist eine zeitgenössische Designmarke, die Ästhetik und Funktionalität im Bereich Innenarchitektur und Möbeldesign vereint. Seit seiner Gründung zielt es darauf ab, Lebensräume nicht nur visuell, sondern auch in Bezug auf Nutzung, Komfort und Nachhaltigkeit zu transformieren. Wir betrachten Design direkt als Lebensqualität. Dieser Ansatz ermöglicht es ERNADO, in allen Projekten eine konsistente, ausgewogene und zeitlose Sprache zu schaffen.'}
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-light mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {currentLanguage === 'tr' && 'Vizyonumuz'}
                    {currentLanguage === 'en' && 'Our Vision'}
                    {currentLanguage === 'de' && 'Unsere Vision'}
                  </h3>
                  <p className="text-lg">
                    {currentLanguage === 'tr' && 'Uluslararası ölçekte tanınan , çağdaş yaşam alanları konusunda referans gösterilen bir tasarım ve üretim markası olmak. ERNADO; İç mimari , mobilya ve mekansal tasarımı tek bir marka dili altında birleştirerek , küresel pazarda kalıcı bir konum edinmeyi hedefler.Zamana dirençli tasarım anlayışımızla , geçici trendlerin ötesine geçen bir marka mirası oluşturmayı amaçlarız. AR teknolojisi ile tasarımlarımızı görselleştirerek, müşterilerimize projelerini deneyimleme fırsatı sunuyoruz.'}
                    {currentLanguage === 'en' && 'With natural materials and sustainable design philosophy, we aim to create unique and timeless spaces in every project. By visualizing our designs with AR technology, we offer our clients the opportunity to experience their projects.'}
                    {currentLanguage === 'de' && 'Mit natürlichen Materialien und nachhaltiger Designphilosophie streben wir danach, in jedem Projekt einzigartige und zeitlose Räume zu schaffen. Durch die Visualisierung unserer Designs mit AR-Technologie bieten wir unseren Kunden die Möglichkeit, ihre Projekte zu erleben.'}
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-light mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {currentLanguage === 'tr' && 'Hizmetlerimiz'}
                    {currentLanguage === 'en' && 'Our Services'}
                    {currentLanguage === 'de' && 'Unsere Dienstleistungen'}
                  </h3>
                  <ul className="space-y-3 text-lg">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#D4AF37] text-xl mt-1"></i>
                      <span>
                        {currentLanguage === 'tr' && 'Mimari Proje Tasarımı'}
                        {currentLanguage === 'en' && 'Architectural Project Design'}
                        {currentLanguage === 'de' && 'Architektonisches Projektdesign'}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#D4AF37] text-xl mt-1"></i>
                      <span>
                        {currentLanguage === 'tr' && 'İç Mekan Tasarımı'}
                        {currentLanguage === 'en' && 'Interior Design'}
                        {currentLanguage === 'de' && 'Innenarchitektur'}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#D4AF37] text-xl mt-1"></i>
                      <span>
                        {currentLanguage === 'tr' && 'Özel Mobilya Tasarımı'}
                        {currentLanguage === 'en' && 'Custom Furniture Design'}
                        {currentLanguage === 'de' && 'Maßgeschneidertes Möbeldesign'}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#D4AF37] text-xl mt-1"></i>
                      <span>
                        {currentLanguage === 'tr' && 'AR Teknolojisi ile Görselleştirme'}
                        {currentLanguage === 'en' && 'AR Technology Visualization'}
                        {currentLanguage === 'de' && 'AR-Technologie-Visualisierung'}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#D4AF37] text-xl mt-1"></i>
                      <span>
                        {currentLanguage === 'tr' && 'Dış Cephe ve Prefabrik Çözümler'}
                        {currentLanguage === 'en' && 'Exterior Facade and Prefab Solutions'}
                        {currentLanguage === 'de' && 'Außenfassaden- und Fertiglösungen'}
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Contact Info */}
                <div className="bg-[#FAF9F7] rounded-2xl p-8 mt-12">
                  <h3 className="text-2xl font-light mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {currentLanguage === 'tr' && 'İletişim'}
                    {currentLanguage === 'en' && 'Contact'}
                    {currentLanguage === 'de' && 'Kontakt'}
                  </h3>
                  <div className="space-y-4 text-lg">
                    <div className="flex items-center gap-3">
                      <i className="ri-map-pin-line text-[#D4AF37] text-xl"></i>
                      <span>Nilüfer, Bursa, Türkiye</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-mail-line text-[#D4AF37] text-xl"></i>
                      <a href="mailto:ernado@outlook.com.tr" className="hover:text-[#D4AF37] transition-colors">
                        ernado@outlook.com.tr
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
